import { formatVND } from "@/lib/format";
import type { MyAffiliateLedger as Ledger } from "@/features/affiliates/my-code-api";
import {
  nextOrderStep,
  nextRevenueStep,
  periodLabel,
  type MilestoneProgress,
} from "@/features/affiliates/milestone-progress";
import { HintNote } from "@/components/HintTip";

export function LedgerSectionTitle({ title, aside }: { title: string; aside: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <h3 className="text-sm font-semibold text-muted-foreground">{title}</h3>
      {aside ? <span className="text-xs text-muted-foreground">{aside}</span> : null}
    </div>
  );
}

/**
 * Tiến độ tới mốc thưởng của QUÝ đang chạy — số do AFF đếm theo đúng luật trao
 * mốc (CEO chốt 3b, 01/10/2026). Chỉ để HIỆN: mốc thật do AFF trao, hiện ở khối
 * "Thưởng mốc đã đạt"; ở đây không tự gắn nhãn "đã đạt".
 */
export function MilestoneProgressSection({
  progress,
  config,
}: {
  progress: MilestoneProgress;
  config: Ledger["config"];
}) {
  const steps = [
    config.orderMilestones.length > 0
      ? (nextOrderStep(config.orderMilestones, progress) ?? "Đã qua mốc số đơn cao nhất của quý")
      : null,
    config.revenueMilestones.length > 0
      ? (nextRevenueStep(config.revenueMilestones, progress) ??
        "Đã qua mốc doanh số cao nhất của quý")
      : null,
  ].filter((step) => step !== null);

  return (
    <section aria-label="Tiến độ mốc thưởng">
      <LedgerSectionTitle title="Tiến độ mốc thưởng" aside={periodLabel(progress.period)} />
      <div className="mt-1.5 space-y-3 rounded-md border border-border bg-surface px-3 py-2.5">
        <div className="text-sm">
          Quý này bạn có <span className="font-semibold">{progress.orders} đơn</span> ·{" "}
          <span className="font-semibold">{formatVND(progress.revenue)}</span> doanh số được tính
        </div>
        {steps.map((step) =>
          typeof step === "string" ? (
            <div key={step} className="text-sm font-medium text-[var(--color-success)]">
              {step}
            </div>
          ) : (
            <div key={step.title}>
              <div className="flex items-baseline justify-between gap-3 text-sm">
                <span className="font-medium">{step.title}</span>
                <span className="text-muted-foreground">
                  thưởng{" "}
                  <span className="font-semibold tabular-nums text-foreground">
                    {formatVND(step.reward)}
                  </span>
                </span>
              </div>
              <div
                className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-border"
                role="progressbar"
                aria-valuenow={step.percent}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={`Tiến độ ${step.title}`}
              >
                <div className="h-full rounded-full bg-brand" style={{ width: `${step.percent}%` }} />
              </div>
              <div className="mt-1 text-xs text-muted-foreground">{step.remaining}</div>
            </div>
          ),
        )}
        <HintNote>
          Chỉ tính đơn khách đã thanh toán đủ trong quý, không tính đơn mỹ phẩm.
        </HintNote>
      </div>
    </section>
  );
}
